import { useState, useMemo, useEffect } from 'react';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/store';
import {
  Plus,
  MagnifyingGlass as Search,
  FunnelSimple as Filter,
  Envelope as Mail,
  Phone,
  Briefcase,
  TrendUp as TrendingUp,
  SquaresFour as LayoutGrid,
  List,
  Spinner,
  UserMinus,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { StatCard } from '@/components/shared/StatCard';
import { RowActions } from '@/components/shared/RowActions';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { InviteMemberModal } from '@/components/shared/InviteMemberModal';
import { updateUserStatus } from '@/lib/usersApi';
import { memberAccountAction } from '@/lib/accountLifecycle';
import { useMattersStore } from '@/lib/mattersStore';

const ROLE_LABEL: Record<string, string> = {
  silk: 'KC / Silk',
  junior: 'Junior',
  pupil: 'Pupil',
  door_tenant: 'Door tenant',
};

const AVAILABILITY_LABEL: Record<string, string> = {
  available: 'Available',
  partial: 'Partial',
  busy: 'In court',
  on_leave: 'On leave',
  unavailable: 'Unavailable',
};

const ROLE_FILTERS: { label: string; value: string | null }[] = [
  { label: 'All', value: null },
  { label: 'Silks', value: 'silk' },
  { label: 'Juniors', value: 'junior' },
  { label: 'Pupils', value: 'pupil' },
  { label: 'Door tenants', value: 'door_tenant' },
];

export default function BarristersPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [availFilter, setAvailFilter] = useState<string | null>(null);
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  // Fetch barristers from real API
  const [inviteOpen, setInviteOpen] = useState(false);
  const barristers = useApiBarristersStore((s) => s.barristers);
  const fetchBarristers = useApiBarristersStore((s) => s.fetch);
  const refetchBarristers = useApiBarristersStore((s) => s.refetch);
  const loading = useApiBarristersStore((s) => s.loading);
  const loaded = useApiBarristersStore((s) => s.loaded);

  const handleDeactivate = async (userId: string, name: string) => {
    try {
      // Deactivate means deactivate (status 'inactive', recorded server-side). The member stays
      // listed under Settings → Manage members as "Deactivated", where Reactivate brings them back.
      await updateUserStatus(userId, 'inactive');
      await refetchBarristers();
      toast.success(`${name} deactivated`);
    } catch {
      toast.error('Could not deactivate this person');
    }
  };

  // Fetch matters to compute case counts per barrister
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);

  useEffect(() => {
    fetchBarristers();
    fetchMatters();
  }, [fetchBarristers, fetchMatters]);

  // Compute case counts per barrister from real matters
  const caseCountByBarrister = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of matters) {
      if (m.barrister && isOpenMatterStatus(m.status)) {
        counts[m.barrister] = (counts[m.barrister] || 0) + 1;
      }
    }
    return counts;
  }, [matters]);

  // Compute stats from real data
  const stats = useMemo(() => {
    const total = barristers.length;
    const silks = barristers.filter((b) => b.user.memberType === 'silk').length;
    const juniors = barristers.filter((b) => b.user.memberType === 'junior').length;
    const pupils = barristers.filter((b) => b.user.memberType === 'pupil').length;
    const available = barristers.filter((b) => b.availability === 'available' || b.availability === 'partial').length;
    const practiceAreas = new Set(barristers.flatMap((b) => b.practiceAreas.map((pa) => pa.name))).size;
    return { total, silks, juniors, pupils, available, practiceAreas };
  }, [barristers]);

  const allPracticeAreas = useMemo(
    () => Array.from(new Set(barristers.flatMap((b) => b.practiceAreas.map((pa) => pa.name)))).sort(),
    [barristers],
  );

  const activeFilterCount = [availFilter, practiceAreaFilter].filter(Boolean).length;

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return barristers.filter((b) => {
      if (roleFilter && b.user.memberType !== roleFilter) return false;
      if (availFilter && b.availability !== availFilter) return false;
      if (practiceAreaFilter && !b.practiceAreas.some((pa) => pa.name === practiceAreaFilter)) return false;
      if (q) {
        const haystack = [
          b.fullName,
          b.practiceAreas.map((pa) => pa.name).join(' '),
          String(b.callYear ?? ''),
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [barristers, searchQuery, roleFilter, availFilter, practiceAreaFilter]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Barristers"
        subtitle={`${stats.total} members of chambers — silks, juniors, pupils and door tenants`}
        actions={
          <>
            {/* Was a "Public profiles" button to /admin/public-site — that page is the empty
                "coming soon" website-builder shell RC-10 hid from the nav, so the button was a
                dead end. A barrister's LIVE public profile is reachable from their detail page
                (the Profile button, which opens the published /barristers/<slug>). */}
            <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setInviteOpen(true)}>Add barrister</Button>
          </>
        }
      />
      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        defaultMemberType="silk"
        onSuccess={() => { void refetchBarristers(); }}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard label="Total members" value={stats.total} hint={`${stats.silks} silks · ${stats.juniors} juniors · ${stats.pupils} pupils`} />
        <StatCard label="Available now" value={stats.available} hint="ready to take briefs" />
        <StatCard label="Active matters" value={Object.values(caseCountByBarrister).reduce((a, b) => a + b, 0)} hint="across chambers" />
        <StatCard label="Practice areas" value={stats.practiceAreas} hint="across chambers" />
      </div>

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search by name, practice area or call year…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs">
            {ROLE_FILTERS.map((rf) => (
              <button
                key={rf.label}
                className={`rounded-sm px-3 py-1.5 font-medium ${
                  roleFilter === rf.value ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setRoleFilter(rf.value)}
              >
                {rf.label}
              </button>
            ))}
          </div>
          <Button
            variant={filtersOpen || activeFilterCount > 0 ? 'primary' : 'outline'}
            size="md"
            leftIcon={<Filter className="h-4 w-4" />}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
          </Button>
          <div className="flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
            <button
              className={`rounded-sm p-1.5 ${view === 'grid' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setView('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              className={`rounded-sm p-1.5 ${view === 'list' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
        {filtersOpen && (
          <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-3 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Availability</span>
              <div className="flex flex-wrap items-center gap-1">
                {([null, 'available', 'partial', 'busy', 'on_leave', 'unavailable'] as const).map((v) => (
                  <button
                    key={v ?? 'all'}
                    onClick={() => setAvailFilter(v)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      availFilter === v
                        ? 'bg-brand-600 text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {v === null ? 'All' : AVAILABILITY_LABEL[v]}
                  </button>
                ))}
              </div>
            </div>
            {allPracticeAreas.length > 0 && (
              <div className="flex items-start gap-2">
                <span className="whitespace-nowrap pt-1 text-xs font-semibold text-muted-foreground">Practice area</span>
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    onClick={() => setPracticeAreaFilter(null)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      practiceAreaFilter === null
                        ? 'bg-brand-600 text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    All
                  </button>
                  {allPracticeAreas.map((pa) => (
                    <button
                      key={pa}
                      onClick={() => setPracticeAreaFilter(pa === practiceAreaFilter ? null : pa)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                        practiceAreaFilter === pa
                          ? 'bg-brand-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {pa}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setAvailFilter(null); setPracticeAreaFilter(null); }}
                className="self-center text-xs text-muted-foreground underline hover:text-foreground"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </Card>

      {/* Loading state */}
      {loading && !loaded && (
        <Card className="py-16 text-center">
          <CardContent>
            <Spinner className="mx-auto mb-3 h-10 w-10 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading barristers...</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <Card className="py-16 text-center">
          <CardContent>
            <Search className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-lg font-semibold text-foreground">No barristers found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {barristers.length === 0 ? 'Add your first barrister to get started.' : 'Try adjusting your search or filter.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Grid view */}
      {!loading && filtered.length > 0 && view === 'grid' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((b) => {
            const caseCount = caseCountByBarrister[b.fullName] || caseCountByBarrister[b.shortName] || 0;
            const availLabel = AVAILABILITY_LABEL[b.availability] || b.availability;
            return (
              <Card
                key={b.id}
                className="cursor-pointer transition hover:border-brand-300 hover:shadow-pop"
                onClick={() => navigate(`${basePath}/barristers/${b.id}`)}
              >
                <CardContent className="p-5">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar src={b.user.avatarUrl ?? undefined} fallback={b.initials} size="lg" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-[15px] font-semibold text-foreground">{b.fullName}</div>
                          {b.user.status !== 'active' && (
                            <Badge variant="default" className="px-1.5 py-0 text-[10px] uppercase text-muted-foreground">{b.user.status}</Badge>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {ROLE_LABEL[b.user.memberType] || b.user.memberType} {b.callYear ? `· Called ${b.callYear}` : ''}
                        </div>
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <RowActions
                        onView={() => navigate(`${basePath}/barristers/${b.id}`)}
                        onEdit={() => navigate(`${basePath}/barristers/${b.id}`)}
                        onDelete={memberAccountAction(user, { id: b.user.id, memberType: b.user.memberType, status: b.user.status })?.kind === 'suspend' && b.user.status === 'active' ? () => handleDeactivate(b.user.id, b.fullName) : undefined}
                        viewLabel="View profile"
                        deleteLabel="Deactivate"
                        deleteIcon={<UserMinus className="h-4 w-4" />}
                        deleteTone="neutral"
                        deleteConfirm={{
                          title: `Deactivate ${b.fullName}?`,
                          message: `${b.fullName} will lose access immediately. Their account and records are kept, and they can be reactivated from Settings → Manage members.`,
                          confirmLabel: 'Deactivate',
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-4 flex flex-wrap gap-1">
                    {b.practiceAreas.slice(0, 3).map((pa) => (
                      <Badge key={pa.id}>{pa.name}</Badge>
                    ))}
                    {b.practiceAreas.length > 3 && (
                      <Badge variant="default">+{b.practiceAreas.length - 3}</Badge>
                    )}
                  </div>

                  <div className="mb-4 grid grid-cols-2 gap-3 border-y border-border py-3">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active matters</div>
                      <div className="tabular mt-0.5 flex items-center gap-1 text-base font-semibold text-foreground">
                        <Briefcase className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
                        {caseCount}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Seniority</div>
                      <div className="tabular mt-0.5 flex items-center gap-1 text-base font-semibold text-foreground">
                        <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                        {b.seniority} yrs
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Badge
                      variant={
                        b.availability === 'available' || b.availability === 'partial'
                          ? 'success'
                          : b.availability === 'busy'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {availLabel}
                    </Badge>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={`mailto:${b.user.email}`}
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Email"
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                      {b.user.phone && (
                        <a
                          href={`tel:${b.user.phone}`}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Call"
                        >
                          <Phone className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* List / table view */}
      {!loading && filtered.length > 0 && view === 'list' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Call year</th>
                  <th className="px-4 py-3">Practice areas</th>
                  <th className="px-4 py-3 text-right">Active cases</th>
                  <th className="px-4 py-3 text-right">Seniority</th>
                  <th className="px-4 py-3">Availability</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const caseCount = caseCountByBarrister[b.fullName] || caseCountByBarrister[b.shortName] || 0;
                  const availLabel = AVAILABILITY_LABEL[b.availability] || b.availability;
                  return (
                    <tr
                      key={b.id}
                      className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50"
                      onClick={() => navigate(`${basePath}/barristers/${b.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar src={b.user.avatarUrl ?? undefined} fallback={b.initials} size="sm" />
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{b.fullName}</span>
                            {b.user.status !== 'active' && (
                              <Badge variant="default" className="px-1.5 py-0 text-[10px] uppercase text-muted-foreground">{b.user.status}</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{ROLE_LABEL[b.user.memberType] || b.user.memberType}</td>
                      <td className="px-4 py-3 tabular text-muted-foreground">{b.callYear ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {b.practiceAreas.slice(0, 3).map((pa) => (
                            <Badge key={pa.id}>{pa.name}</Badge>
                          ))}
                          {b.practiceAreas.length > 3 && (
                            <Badge variant="default">+{b.practiceAreas.length - 3}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular text-foreground">{caseCount}</td>
                      <td className="px-4 py-3 text-right tabular text-foreground">{b.seniority} yrs</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={
                            b.availability === 'available' || b.availability === 'partial'
                              ? 'success'
                              : b.availability === 'busy'
                              ? 'warning'
                              : 'default'
                          }
                        >
                          {availLabel}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={`mailto:${b.user.email}`}
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="Email"
                          >
                            <Mail className="h-4 w-4" />
                          </a>
                          {b.user.phone && (
                            <a
                              href={`tel:${b.user.phone}`}
                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              title="Call"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          )}
                          <RowActions
                            onView={() => navigate(`${basePath}/barristers/${b.id}`)}
                            onEdit={() => navigate(`${basePath}/barristers/${b.id}`)}
                            onDelete={memberAccountAction(user, { id: b.user.id, memberType: b.user.memberType, status: b.user.status })?.kind === 'suspend' && b.user.status === 'active' ? () => handleDeactivate(b.user.id, b.fullName) : undefined}
                            viewLabel="View profile"
                            deleteLabel="Deactivate"
                            deleteIcon={<UserMinus className="h-4 w-4" />}
                            deleteTone="neutral"
                            deleteConfirm={{
                              title: `Deactivate ${b.fullName}?`,
                              message: `${b.fullName} will lose access immediately. Their account and records are kept, and they can be reactivated from Settings → Manage members.`,
                              confirmLabel: 'Deactivate',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
